import React, { useState } from 'react';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';
import { 
  ArrowRight,
  Boxes,
  HelpCircle,
  Search,
  Newspaper,
  LayoutList
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { TOOLS_LIST } from '../tools/ToolsDirectoryPage';
import { useTenant } from '../../contexts/TenantContext';
import { useModule } from '../../hooks/useModules';

export function ToolboxDirectoryPage() {
  const navigate = useNavigate();
  const { node, isGlobal } = useTenant();
  const [searchQuery, setSearchQuery] = useState('');
  
  const hasActiveNode = Boolean(node && !isGlobal && node.id !== 'platform-root');
  const hasNewsletter = useModule('NEWSLETTER');
  const hasDiscussions = useModule('DISCUSSIONS');
  
  const activeCategory = node?.config?.industryCategory;
  const [activeTab, setActiveTab] = useState<'recommended' | 'all'>(activeCategory ? 'recommended' : 'all');

  // Filter tools based on search and tab
  const filteredTools = TOOLS_LIST.filter(tool => {
    // Tab filter
    if (activeTab === 'recommended' && activeCategory) {
      if (tool.category.toLowerCase() !== activeCategory.toLowerCase()) {
        return false;
      }
    }

    // Search filter
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return tool.title.toLowerCase().includes(q) || 
           tool.description.toLowerCase().includes(q) || 
           tool.category.toLowerCase().includes(q);
  });

  return (
    <div className="max-w-7xl mx-auto space-y-8 animate-in fade-in zoom-in-95 duration-300 pb-16">
      {/* Header */}
      <header className="mb-8 border-b-[4px] border-black dark:border-white pb-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FFD600] text-black px-3 py-1 border-[2px] border-black mb-4">
              <Boxes size={14} />
              <span className="text-[10px] font-black uppercase tracking-widest">Admin Toolbox</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-black font-display uppercase tracking-tighter text-black dark:text-white leading-none">
              Member Toolbox
            </h1>
            <p className="text-gray-600 dark:text-gray-400 font-bold mt-4 max-w-2xl">
              Interactive calculators and utility frameworks to streamline governance complexity. These are the same tools available publicly, accessible right from your dashboard.
            </p>
          </div>
          
          {/* Search */}
          <div className="w-full md:w-72 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search tools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white dark:bg-black border-[3px] border-black dark:border-white text-sm font-bold placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#0044FF] focus:border-transparent transition-all rounded-none"
            />
          </div>
        </div>

        {/* Tabs */}
        {activeCategory && (
          <div className="flex flex-wrap gap-4 pt-4 mt-6 border-t-2 border-black dark:border-white border-dashed">
            <button
              onClick={() => setActiveTab('recommended')}
              className={cn(
                "px-6 py-2 font-black uppercase tracking-widest text-sm border-2 border-black dark:border-white transition-colors cursor-pointer",
                activeTab === 'recommended' 
                  ? "bg-[#0044FF] text-white dark:bg-[#FFD600] dark:text-black shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]"
                  : "bg-white text-black hover:bg-gray-100 dark:bg-black dark:text-white hover:dark:bg-slate-900"
              )}
            >
              Recommended for {activeCategory}
            </button>
            <button
              onClick={() => setActiveTab('all')}
              className={cn(
                "px-6 py-2 font-black uppercase tracking-widest text-sm border-2 border-black dark:border-white transition-colors cursor-pointer",
                activeTab === 'all' 
                  ? "bg-[#0044FF] text-white dark:bg-[#FFD600] dark:text-black shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#fff]"
                  : "bg-white text-black hover:bg-gray-100 dark:bg-black dark:text-white hover:dark:bg-slate-900"
              )}
            >
              All Tools
            </button>
          </div>
        )}
      </header>

      {/* Featured In-App Studios - only visible when active organization is present */}
      {hasActiveNode && (hasNewsletter || hasDiscussions) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {hasNewsletter && (
            <div className="p-6 bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#FFFFFF] flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#0044FF] text-white px-2 py-0.5 text-[9px] font-black uppercase tracking-widest font-mono">In-App Studio</span>
                  <span className="text-[10px] font-mono text-gray-500 uppercase font-bold">Email Builder</span>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 border-2 border-black dark:border-white bg-[#FFD600] flex items-center justify-center text-black">
                    <Newspaper size={20} />
                  </div>
                  <h3 className="text-xl font-black font-display uppercase tracking-tight">Newsletter Maker</h3>
                </div>
                <p className="text-sm font-bold text-gray-600 dark:text-gray-400">
                  Draft, customize, and distribute branded digital newsletters with drag-and-drop sections directly to member lists.
                </p>
              </div>
              <div className="pt-4 mt-4 border-t-2 border-black dark:border-white">
                <Link
                  to="/newsletter"
                  className="inline-flex items-center gap-2 bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-widest px-4 py-2 border-2 border-black dark:border-white hover:bg-[#0044FF] hover:text-white transition-colors"
                >
                  Open Newsletter Studio <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          )}

          {hasDiscussions && (
            <div className="p-6 bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[6px_6px_0px_#000000] dark:shadow-[6px_6px_0px_#FFFFFF] flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-[#0044FF] text-white px-2 py-0.5 text-[9px] font-black uppercase tracking-widest font-mono">In-App Forum</span>
                  <span className="text-[10px] font-mono text-gray-500 uppercase font-bold">Structured Feed</span>
                </div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 border-2 border-black dark:border-white bg-[#0044FF] flex items-center justify-center text-white">
                    <LayoutList size={20} />
                  </div>
                  <h3 className="text-xl font-black font-display uppercase tracking-tight">Message Board</h3>
                </div>
                <p className="text-sm font-bold text-gray-600 dark:text-gray-400">
                  Organize long-form discussions, board proposals, and categorized committee feedback outside noisy chat rooms.
                </p>
              </div>
              <div className="pt-4 mt-4 border-t-2 border-black dark:border-white">
                <Link
                  to="/discussions"
                  className="inline-flex items-center gap-2 bg-black text-white dark:bg-white dark:text-black font-black uppercase text-xs tracking-widest px-4 py-2 border-2 border-black dark:border-white hover:bg-[#0044FF] hover:text-white transition-colors"
                >
                  Open Message Board <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      {filteredTools.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredTools.map((tool, idx) => (
            <motion.div
              key={tool.slug}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: (idx % 12) * 0.05 }}
              className="bg-white dark:bg-black border-[4px] border-black dark:border-white shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff] p-6 hover:translate-x-[-4px] hover:translate-y-[-4px] hover:shadow-[12px_12px_0px_#000] dark:hover:shadow-[12px_12px_0px_#fff] transition-all rounded-none group flex flex-col justify-between relative min-h-[280px]"
            >
              <Link
                to={`/toolbox/${tool.slug}`}
                className="absolute inset-0 z-10"
                aria-label={`Open tool: ${tool.title}`}
              />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 border-[3px] border-black dark:border-white bg-white dark:bg-black flex items-center justify-center relative z-20">
                    {tool.icon}
                  </div>
                  <div className="flex gap-2 relative z-20 pointer-events-none">
                    <span className="border-[2px] border-black dark:border-white px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-white dark:bg-black text-black dark:text-white">{tool.category}</span>
                    {tool.badge && (
                      <span className="border-[2px] border-black dark:border-white px-2 py-0.5 text-[9px] font-black uppercase tracking-widest bg-[#FFD600] text-black">{tool.badge}</span>
                    )}
                  </div>
                </div>
                <h3 className="text-xl font-black font-display uppercase tracking-tighter text-black dark:text-white group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors leading-tight">
                  {tool.title}
                </h3>
                <p className="text-sm font-bold text-gray-600 dark:text-gray-400 leading-snug line-clamp-3">
                  {tool.description}
                </p>
              </div>
              <div className="pt-4 mt-6 border-t-[3px] border-black dark:border-white flex justify-between items-center text-[10px] font-black uppercase tracking-widest text-black dark:text-white group-hover:text-[#0044FF] dark:group-hover:text-[#FFD600] transition-colors relative z-20 pointer-events-none">
                <span className="flex items-center gap-1">
                  <Boxes className="w-3.5 h-3.5" /> OPEN
                </span>
                <div className="w-8 h-8 border-[3px] border-black dark:border-white flex items-center justify-center group-hover:bg-[#0044FF] group-hover:border-[#0044FF] dark:group-hover:bg-[#FFD600] dark:group-hover:border-[#FFD600] group-hover:text-white dark:group-hover:text-black transition-all bg-white dark:bg-black text-black dark:text-white">
                  <ArrowRight className="w-4 h-4" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div className="py-20 text-center border-[4px] border-black dark:border-white border-dashed bg-gray-50 dark:bg-[#050505]">
          <Search className="w-12 h-12 text-gray-400 mx-auto mb-4 opacity-50" />
          <h3 className="text-2xl font-black uppercase tracking-tighter mb-2">No tools found</h3>
          <p className="text-gray-500 font-bold">Try adjusting your search terms.</p>
        </div>
      )}

      {/* Footer CTA */}
      <div className="mt-16 text-center max-w-lg mx-auto space-y-6 relative p-8 border-[4px] border-black dark:border-white bg-[#F5F5F0] dark:bg-[#0A0A0A] shadow-[8px_8px_0px_#000] dark:shadow-[8px_8px_0px_#fff]">
        <HelpCircle className="w-12 h-12 text-black dark:text-white mx-auto mb-2" />
        <h4 className="font-black font-display uppercase text-2xl tracking-tight">Need a custom formula?</h4>
        <p className="text-sm font-bold text-gray-700 dark:text-gray-300 leading-relaxed">
          If your organization administers complex dues schedules, custom regional levies, or non-traditional voting weights, our support team can help bootstrap special workflows.
        </p>
        <div className="pt-2">
          <button onClick={() => navigate('/support')} className="bg-black text-white dark:bg-white dark:text-black border-[4px] border-black dark:border-white font-black uppercase tracking-widest px-8 py-4 transition-all shadow-[6px_6px_0px_#0044FF] dark:shadow-[6px_6px_0px_#FFD600] hover:translate-x-[-2px] hover:translate-y-[-2px] hover:shadow-[8px_8px_0px_#0044FF] dark:hover:shadow-[8px_8px_0px_#FFD600] rounded-none cursor-pointer relative">
            Contact Support
          </button>
        </div>
      </div>
    </div>
  );
}
